import { useTranslation } from 'react-i18next';
import { Building2, MapPin, Star } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { HotelVendorBadge } from '@/components/hotels/hotel-vendor-badge';
import { cn } from '@/lib/cn';
import type { HotelOption } from '@/lib/allocation/types';
import type { Reservation } from '@/lib/booking/types';
import { HoldRow } from './hold-row';
import { ReservationRow } from './reservation-row';
import type { GroupLock, PendingHold } from './types';
import { isPriced } from '@/lib/allocation/price';
import { Unpriced } from './unpriced';

// A policy-ranked hotel offer rendered as a drop zone: ★/price/distance/remaining
// capacity + the assigned groups' HOLD/reservation rows. Pure presentation —
// drag/drop + lifecycle callbacks come via props.

export interface HotelDropCardProps {
  offer: HotelOption;
  assignedRooms: number;
  holdsHere: PendingHold[];
  reservationsHere: Reservation[];
  dragActive: boolean;
  isOver: boolean;
  now: number;
  labelForSubCase: (urn: string) => string;
  lockForSubCase: (urn: string) => GroupLock;
  cancelPending: boolean;
  onDragOver: () => void;
  onDragLeave: () => void;
  onDrop: () => void;
  onConfirm: (hold: PendingHold) => void;
  onRelease: (hold: PendingHold) => void;
  onRehold: (hold: PendingHold) => void;
  onDismiss: (hold: PendingHold) => void;
  onCancel: (reservationUrn: string) => void;
}

export function HotelDropCard({
  offer,
  assignedRooms,
  holdsHere,
  reservationsHere,
  dragActive,
  isOver,
  now,
  labelForSubCase,
  lockForSubCase,
  cancelPending,
  onDragOver,
  onDragLeave,
  onDrop,
  onConfirm,
  onRelease,
  onRehold,
  onDismiss,
  onCancel,
}: HotelDropCardProps) {
  const { t } = useTranslation('cases');
  const remaining = Math.max(0, offer.availableRooms - assignedRooms);
  const hasAssignments = holdsHere.length > 0 || reservationsHere.length > 0;

  return (
    <Card
      onDragOver={(e) => {
        if (!dragActive) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        onDragOver();
      }}
      onDragLeave={onDragLeave}
      onDrop={(e) => {
        e.preventDefault();
        onDrop();
      }}
      className={cn(
        'transition-colors',
        isOver && dragActive && 'border-primary ring-2 ring-primary/40',
        hasAssignments && 'border-emerald-500/40',
      )}
      data-testid="hotel-drop-card"
    >
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <CardTitle className="flex items-center gap-2 text-base">
              <Building2 className="h-4 w-4 text-primary" aria-hidden />
              <span className="truncate">{offer.name}</span>
              <span className="flex items-center text-amber-500">
                {Array.from({ length: offer.stars }).map((_, i) => (
                  <Star key={i} className="h-3 w-3 fill-current" aria-hidden />
                ))}
              </span>
              <HotelVendorBadge urn={offer.hotelUrn} className="shrink-0" />
            </CardTitle>
            <p className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <MapPin className="h-3 w-3" aria-hidden />
                {offer.distanceKm.toFixed(1)} km
              </span>
              ·
              {isPriced(offer.ratePerRoomUSD) ? (
                <span className="font-semibold tabular-nums text-foreground">
                  ${offer.ratePerRoomUSD}
                  <span className="font-normal text-muted-foreground">{t('common:holds.perNight')}</span>
                </span>
              ) : (
                <Unpriced />
              )}
            </p>
          </div>
          <Badge variant={remaining > 0 ? 'muted' : 'warning'} size="sm">
            {t('hotelCard.roomsFree', { remaining, total: offer.availableRooms })}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        {!hasAssignments ? (
          <div
            className={cn(
              'rounded-md border border-dashed py-4 text-center text-xs',
              isOver && dragActive
                ? 'border-primary text-primary'
                : 'border-border text-muted-foreground',
            )}
          >
            {dragActive ? t('hotelCard.dropHere') : t('hotelCard.dragHere')}
          </div>
        ) : null}

        {holdsHere.map((h) => (
          <HoldRow
            key={h.id}
            hold={h}
            now={now}
            groupLabel={labelForSubCase(h.subCaseUrn)}
            lock={lockForSubCase(h.subCaseUrn)}
            onConfirm={() => onConfirm(h)}
            onRelease={() => onRelease(h)}
            onRehold={() => onRehold(h)}
            onDismiss={() => onDismiss(h)}
          />
        ))}
        {reservationsHere.map((r) => (
          <ReservationRow
            key={r.reservationUrn}
            reservation={r}
            cancelPending={cancelPending}
            onCancel={() => onCancel(r.reservationUrn)}
          />
        ))}
      </CardContent>
    </Card>
  );
}
